import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '../store/gameStore';

interface CompanionInventoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CompanionInventoryModal: React.FC<CompanionInventoryModalProps> = ({ isOpen, onClose }) => {
  const {
    companions,
    formatNumber,
    getOreCounts,
    getCompanionArmor,
    getCompanionWeapons,
    equipArmor,
    unequipArmor,
    equipWeapon,
    unequipWeapon,
    getCraftableArmor,
    getCraftableWeapons,
    tradeItemForExperience,
    getItemExperienceValue,
  } = useGameStore();

  // Funktionen für verfügbare (gecraftete) Rüstungen und Waffen
  const getAvailableArmor = () => {
    if (!companions.forge?.armor) return [];
    return Object.values(companions.forge.armor).filter(armor => !armor.isCrafting && !armor.equippedBy);
  };

  const getAvailableWeapons = () => {
    if (!companions.forge?.weapons) return [];
    return Object.values(companions.forge.weapons).filter(weapon => !weapon.isCrafting && !weapon.equippedBy);
  };

  const [selectedTab, setSelectedTab] = useState<'resources' | 'armor' | 'weapons' | 'items' | 'experience'>('resources');
  const [selectedCompanion, setSelectedCompanion] = useState<string | null>(null);
  const [showCompanionSelection, setShowCompanionSelection] = useState(false);
  const [showAvailableArmor, setShowAvailableArmor] = useState(false);
  const [showAvailableWeapons, setShowAvailableWeapons] = useState(false);
  const [showExperienceCompanionSelection, setShowExperienceCompanionSelection] = useState(false);
  const [showAllAvailableArmor, setShowAllAvailableArmor] = useState(false);
  const [showAllAvailableWeapons, setShowAllAvailableWeapons] = useState(false);
  const [selectedXpType, setSelectedXpType] = useState<'normal' | 'arena'>('normal');

  const oreCounts = getOreCounts() || { iron: 0, steel: 0, mithril: 0, adamantium: 0 };

  const getOreIcon = (oreType: string) => {
    switch (oreType) {
      case 'iron': return '🪨';
      case 'steel': return '⚒️';
      case 'mithril': return '💎';
      case 'adamantium': return '🔮';
      default: return '🪨';
    }
  };

  const getArmorIcon = (armorType: string) => {
    switch (armorType) {
      case 'helmet': return '⛑️';
      case 'chestplate': return '🛡️';
      case 'greaves': return '🦵';
      case 'boots': return '👢';
      case 'shield': return '🛡️';
      default: return '⚔️';
    }
  };

  const getWeaponIcon = (weaponType: string) => {
    switch (weaponType) {
      case 'sword': return '⚔️';
      case 'axe': return '🪓';
      case 'mace': return '🔨';
      case 'dagger': return '🗡️';
      case 'bow': return '🏹';
      case 'staff': return '🦯';
      default: return '⚔️';
    }
  };

  const getMaterialColor = (material: string) => {
    switch (material) {
      case 'iron': return 'from-gray-400 to-gray-500';
      case 'steel': return 'from-blue-400 to-blue-500';
      case 'mithril': return 'from-purple-400 to-purple-500';
      case 'adamantium': return 'from-red-400 to-red-500';
      default: return 'from-gray-400 to-gray-500';
    }
  };

  const handleEquipArmor = (armorId: string, companionId: string) => {
    equipArmor(armorId, companionId);
  };

  const handleEquipWeapon = (weaponId: string, companionId: string) => {
    equipWeapon(weaponId, companionId);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence key="inventory-modal">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              📦 Begleiter-Inventar
            </h2>
            <motion.button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-2xl"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              ✕
            </motion.button>
          </div>

          {/* Tab-Navigation */}
          <div className="grid grid-cols-5 gap-1 mb-6">
            {[
              { key: 'resources', icon: '🪨' },
              { key: 'armor', icon: '🛡️' },
              { key: 'weapons', icon: '⚔️' },
              { key: 'items', icon: '📦' },
              { key: 'experience', icon: '⭐' },
            ].map((tab) => (
              <motion.button
                key={tab.key}
                onClick={() => setSelectedTab(tab.key as any)}
                className={`py-2 sm:py-3 px-1 sm:px-2 rounded-lg font-medium transition-all duration-200 text-center ${
                  selectedTab === tab.key
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="text-lg">{tab.icon}</span>
              </motion.button>
            ))}
          </div>

          {/* Tab-Inhalte */}
          <AnimatePresence mode="wait" key="tab-content">
            {selectedTab === 'resources' && (
              <motion.div
                key="resources"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-4"
              >
                <h3 className="text-xl font-bold text-white mb-4 text-center">🪨 Erz-Ressourcen</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {Object.entries(oreCounts).map(([oreType, count]) => (
                    <motion.div
                      key={oreType}
                      className="bg-gradient-to-br from-gray-700 to-gray-800 rounded-lg p-4 text-center border border-gray-600"
                      whileHover={{ scale: 1.02 }}
                    >
                      <div className="text-3xl mb-2">{getOreIcon(oreType)}</div>
                      <div className="text-sm font-medium capitalize text-gray-300 mb-1">
                        {oreType === 'iron' ? 'Eisen' : oreType === 'steel' ? 'Stahl' : oreType === 'mithril' ? 'Mithril' : 'Adamantium'}
                      </div>
                      <div className="text-xl font-bold text-white">{formatNumber(count)}</div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {selectedTab === 'armor' && (
              <motion.div
                key="armor"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-4"
              >
                <h3 className="text-xl font-bold text-white mb-4 text-center">🛡️ Rüstungen</h3>
                
                {/* Begleiter-Auswahl - Zusammenklappbar */}
                <div className="mb-4">
                  <motion.button
                    onClick={() => setShowCompanionSelection(!showCompanionSelection)}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg p-3 font-medium transition-all duration-200 flex items-center justify-between"
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <div className="flex items-center gap-2">
                      <span>👥</span>
                      <span>
                        {selectedCompanion 
                          ? `Ausgewählt: ${companions.discovered.find(c => c.id === selectedCompanion)?.name}`
                          : 'Begleiter auswählen'
                        }
                      </span>
                    </div>
                    <motion.div
                      animate={{ rotate: showCompanionSelection ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      ▼
                    </motion.div>
                  </motion.button>

                  <AnimatePresence key="companion-selection">
                    {showCompanionSelection && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mt-3 overflow-hidden"
                      >
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                          {companions.discovered.map((companion) => (
                            <motion.button
                              key={companion.id}
                              onClick={() => {
                                setSelectedCompanion(companion.id);
                                setShowCompanionSelection(false);
                              }}
                              className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                                selectedCompanion === companion.id
                                  ? 'border-blue-500 bg-blue-600 bg-opacity-20'
                                  : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                              }`}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              <div className="flex items-center gap-2">
                                <div className="text-lg">
                                  {companion.icon.startsWith('data:') ? (
                                    <img 
                                      src={companion.icon} 
                                      alt={companion.name}
                                      className="w-6 h-6 object-contain"
                                      style={{ display: 'inline-block' }}
                                    />
                                  ) : (
                                    companion.icon
                                  )}
                                </div>
                                <div className="text-left">
                                  <div className="text-sm font-medium text-white">{companion.name}</div>
                                  <div className="text-xs text-gray-400">Level {companion.level || 1}</div>
                                </div>
                              </div>
                            </motion.button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Verfügbare Rüstungen - Zusammenklappbar */}
                {selectedCompanion && (
                  <div>
                    <motion.button
                      onClick={() => setShowAvailableArmor(!showAvailableArmor)}
                      className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg p-3 font-medium transition-all duration-200 flex items-center justify-between mb-3"
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                    >
                      <div className="flex items-center gap-2">
                        <span>🛡️</span>
                        <span>
                          Verfügbare Rüstungen ({getAvailableArmor().length})
                        </span>
                      </div>
                      <motion.div
                        animate={{ rotate: showAvailableArmor ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        ▼
                      </motion.div>
                    </motion.button>

                    <AnimatePresence key="available-armor">
                      {showAvailableArmor && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          {getAvailableArmor().length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                              {getAvailableArmor().map((armor) => (
                                <motion.div
                                  key={armor.id}
                                  className={`bg-gradient-to-br ${getMaterialColor(armor.material)} rounded-lg p-4 border border-gray-500`}
                                  whileHover={{ scale: 1.02 }}
                                >
                                  <div className="flex items-center gap-3 mb-3">
                                    <span className="text-2xl">{getArmorIcon(armor.type)}</span>
                                    <div>
                                      <div className="font-bold text-white">{armor.name}</div>
                                      <div className="text-sm text-gray-300 capitalize">{armor.material}</div>
                                    </div>
                                  </div>
                                  
                                  <div className="space-y-2 mb-3">
                                    <div className="text-sm text-gray-300">
                                      <span className="font-bold">Verteidigung:</span> +{armor.defense}
                                    </div>
                                    <div className="text-sm text-gray-300">
                                      <span className="font-bold">Haltbarkeit:</span> {armor.durability}/{armor.maxDurability}
                                    </div>
                                  </div>

                                  <motion.button
                                    onClick={() => handleEquipArmor(armor.id, selectedCompanion)}
                                    className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium transition-colors"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                  >
                                    🛡️ Ausrüsten
                                  </motion.button>
                                </motion.div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-8 text-gray-400">
                              <div className="text-3xl mb-3">🛡️</div>
                              <div className="text-lg font-medium mb-2">Keine verfügbaren Rüstungen</div>
                              <div className="text-sm">Gehe zur Schmiede, um Rüstungen zu craften</div>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                {/* Ausgerüstete Rüstungen */}
                {selectedCompanion && (
                  <div className="mt-6">
                    <h4 className="text-lg font-semibold text-white mb-3 text-center">
                      Ausgerüstete Rüstungen
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {getCompanionArmor(selectedCompanion).map((armor) => (
                        <motion.div
                          key={armor.id}
                          className={`bg-gradient-to-br ${getMaterialColor(armor.material)} rounded-lg p-4 border border-gray-500`}
                          whileHover={{ scale: 1.02 }}
                        >
                          <div className="flex items-center gap-3 mb-3">
                            <span className="text-2xl">{getArmorIcon(armor.type)}</span>
                            <div>
                              <div className="font-bold text-white">{armor.name}</div>
                              <div className="text-sm text-gray-300 capitalize">{armor.material}</div>
                            </div>
                          </div>
                          
                          <div className="space-y-2 mb-3">
                            <div className="text-sm text-gray-300">
                              <span className="font-bold">Verteidigung:</span> +{armor.defense}
                            </div>
                            <div className="text-sm text-gray-300">
                              <span className="font-bold">Haltbarkeit:</span> {armor.durability}/{armor.maxDurability}
                            </div>
                          </div>

                          <motion.button
                            onClick={() => unequipArmor(armor.id)}
                            className="w-full py-2 px-4 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium transition-colors"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            ❌ Abnehmen
                          </motion.button>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {selectedTab === 'weapons' && (
              <motion.div
                key="weapons"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-4"
              >
                <h3 className="text-xl font-bold text-white mb-4 text-center">⚔️ Waffen</h3>
                
                {/* Begleiter-Auswahl - Zusammenklappbar */}
                <div className="mb-4">
                  <motion.button
                    onClick={() => setShowCompanionSelection(!showCompanionSelection)}
                    className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white rounded-lg p-3 font-medium transition-all duration-200 flex items-center justify-between"
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <div className="flex items-center gap-2">
                      <span>👥</span>
                      <span>
                        {selectedCompanion 
                          ? `Ausgewählt: ${companions.discovered.find(c => c.id === selectedCompanion)?.name}`
                          : 'Begleiter auswählen'
                        }
                      </span>
                    </div>
                    <motion.div
                      animate={{ rotate: showCompanionSelection ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      ▼
                    </motion.div>
                  </motion.button>

                  <AnimatePresence key="weapon-companion-selection">
                    {showCompanionSelection && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mt-3 overflow-hidden"
                      >
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                          {companions.discovered.map((companion) => (
                            <motion.button
                              key={companion.id}
                              onClick={() => {
                                setSelectedCompanion(companion.id);
                                setShowCompanionSelection(false);
                              }}
                              className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                                selectedCompanion === companion.id
                                  ? 'border-purple-500 bg-purple-600 bg-opacity-20'
                                  : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                              }`}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              <div className="flex items-center gap-2">
                                <div className="text-lg">
                                  {companion.icon.startsWith('data:') ? (
                                    <img 
                                      src={companion.icon} 
                                      alt={companion.name}
                                      className="w-6 h-6 object-contain"
                                      style={{ display: 'inline-block' }}
                                    />
                                  ) : (
                                    companion.icon
                                  )}
                                </div>
                                <div className="text-left">
                                  <div className="text-sm font-medium text-white">{companion.name}</div>
                                  <div className="text-xs text-gray-400">Level {companion.level || 1}</div>
                                </div>
                              </div>
                            </motion.button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Verfügbare Waffen - Zusammenklappbar */}
                {selectedCompanion && (
                  <div>
                    <motion.button
                      onClick={() => setShowAvailableWeapons(!showAvailableWeapons)}
                      className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white rounded-lg p-3 font-medium transition-all duration-200 flex items-center justify-between mb-3"
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                    >
                      <div className="flex items-center gap-2">
                        <span>⚔️</span>
                        <span>
                          Verfügbare Waffen ({getAvailableWeapons().length})
                        </span>
                      </div>
                      <motion.div
                        animate={{ rotate: showAvailableWeapons ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        ▼
                      </motion.div>
                    </motion.button>

                    <AnimatePresence key="available-weapons">
                      {showAvailableWeapons && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          {getAvailableWeapons().length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                              {getAvailableWeapons().map((weapon) => (
                                <motion.div
                                  key={weapon.id}
                                  className={`bg-gradient-to-br ${getMaterialColor(weapon.material)} rounded-lg p-4 border border-gray-500`}
                                  whileHover={{ scale: 1.02 }}
                                >
                                  <div className="flex items-center gap-3 mb-3">
                                    <span className="text-2xl">{getWeaponIcon(weapon.type)}</span>
                                    <div>
                                      <div className="font-bold text-white">{weapon.name}</div>
                                      <div className="text-sm text-gray-300 capitalize">{weapon.material}</div>
                                    </div>
                                  </div>
                                  
                                  <div className="space-y-2 mb-3">
                                    <div className="text-sm text-gray-300">
                                      <span className="font-bold">Angriff:</span> +{weapon.attack}
                                    </div>
                                    <div className="text-sm text-gray-300">
                                      <span className="font-bold">Haltbarkeit:</span> {weapon.durability}/{weapon.maxDurability}
                                    </div>
                                  </div>

                                  <motion.button
                                    onClick={() => handleEquipWeapon(weapon.id, selectedCompanion)}
                                    className="w-full py-2 px-4 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm font-medium transition-colors"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                  >
                                    ⚔️ Ausrüsten
                                  </motion.button>
                                </motion.div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-8 text-gray-400">
                              <div className="text-3xl mb-3">⚔️</div>
                              <div className="text-lg font-medium mb-2">Keine verfügbaren Waffen</div>
                              <div className="text-sm">Gehe zur Schmiede, um Waffen zu craften</div>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                {/* Ausgerüstete Waffen */}
                {selectedCompanion && (
                  <div className="mt-6">
                    <h4 className="text-lg font-semibold text-white mb-3 text-center">
                      Ausgerüstete Waffen
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {getCompanionWeapons(selectedCompanion).map((weapon) => (
                        <motion.div
                          key={weapon.id}
                          className={`bg-gradient-to-br ${getMaterialColor(weapon.material)} rounded-lg p-4 border border-gray-500`}
                          whileHover={{ scale: 1.02 }}
                        >
                          <div className="flex items-center gap-3 mb-3">
                            <span className="text-2xl">{getWeaponIcon(weapon.type)}</span>
                            <div>
                              <div className="font-bold text-white">{weapon.name}</div>
                              <div className="text-sm text-gray-300 capitalize">{weapon.material}</div>
                            </div>
                          </div>
                          
                          <div className="space-y-2 mb-3">
                            <div className="text-sm text-gray-300">
                              <span className="font-bold">Angriff:</span> +{weapon.attack}
                            </div>
                            <div className="text-sm text-gray-300">
                              <span className="font-bold">Haltbarkeit:</span> {weapon.durability}/{weapon.maxDurability}
                            </div>
                          </div>

                          <motion.button
                            onClick={() => unequipWeapon(weapon.id)}
                            className="w-full py-2 px-4 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium transition-colors"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            ❌ Abnehmen
                          </motion.button>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {selectedTab === 'items' && (
              <motion.div
                key="items"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-4"
              >
                <h3 className="text-xl font-bold text-white mb-4 text-center">📦 Alle Gegenstände</h3>
                
                {/* Alle Gegenstände in quadratischen Stapeln */}
                <div className="space-y-6">
                  {/* Erz-Ressourcen */}
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-3 text-center">
                      🪨 Erz-Ressourcen
                    </h4>
                    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
                      {Object.entries(oreCounts).map(([oreType, count]) => (
                        <motion.div
                          key={oreType}
                          className="bg-gradient-to-br from-gray-700 to-gray-800 rounded-lg p-3 text-center border border-gray-600 aspect-square flex flex-col items-center justify-center"
                          whileHover={{ scale: 1.05 }}
                        >
                          <div className="text-2xl mb-1">{getOreIcon(oreType)}</div>
                          <div className="text-[10px] font-medium text-gray-300 mb-1">
                            {oreType === 'iron' ? 'Eisen' : oreType === 'steel' ? 'Stahl' : oreType === 'mithril' ? 'Mithril' : 'Adamantium'}
                          </div>
                          <div className="text-sm font-bold text-white">{formatNumber(count)}</div>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Verfügbare Rüstungen - Zusammenklappbar */}
                  <div>
                    <motion.button
                      onClick={() => setShowAllAvailableArmor(!showAllAvailableArmor)}
                      className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg p-3 font-medium transition-all duration-200 flex items-center justify-between"
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                    >
                      <div className="flex items-center gap-2">
                        <span>🛡️</span>
                        <span>
                          Verfügbare Rüstungen ({getAvailableArmor().length})
                        </span>
                      </div>
                      <motion.div
                        animate={{ rotate: showAllAvailableArmor ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        ▼
                      </motion.div>
                    </motion.button>

                    <AnimatePresence key="all-available-armor">
                      {showAllAvailableArmor && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="mt-3 overflow-hidden"
                        >
                          {getAvailableArmor().length > 0 ? (
                            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
                              {getAvailableArmor().map((armor) => (
                                <motion.div
                                  key={armor.id}
                                  className={`bg-gradient-to-br ${getMaterialColor(armor.material)} rounded-lg p-3 text-center border border-gray-500 aspect-square flex flex-col items-center justify-center`}
                                  whileHover={{ scale: 1.05 }}
                                >
                                  <div className="text-2xl mb-1">{getArmorIcon(armor.type)}</div>
                                  <div className="text-xs font-medium text-white mb-1 truncate w-full">
                                    {armor.name}
                                  </div>
                                  <div className="text-xs text-gray-300">
                                    +{armor.defense}
                                  </div>
                                </motion.div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-4 text-gray-400">
                              <div className="text-2xl mb-2">🛡️</div>
                              <div className="text-sm">Keine verfügbaren Rüstungen</div>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Verfügbare Waffen - Zusammenklappbar */}
                  <div>
                    <motion.button
                      onClick={() => setShowAllAvailableWeapons(!showAllAvailableWeapons)}
                      className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white rounded-lg p-3 font-medium transition-all duration-200 flex items-center justify-between"
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                    >
                      <div className="flex items-center gap-2">
                        <span>⚔️</span>
                        <span>
                          Verfügbare Waffen ({getAvailableWeapons().length})
                        </span>
                      </div>
                      <motion.div
                        animate={{ rotate: showAllAvailableWeapons ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        ▼
                      </motion.div>
                    </motion.button>

                    <AnimatePresence key="all-available-weapons">
                      {showAllAvailableWeapons && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="mt-3 overflow-hidden"
                        >
                          {getAvailableWeapons().length > 0 ? (
                            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
                              {getAvailableWeapons().map((weapon) => (
                                <motion.div
                                  key={weapon.id}
                                  className={`bg-gradient-to-br ${getMaterialColor(weapon.material)} rounded-lg p-3 text-center border border-gray-500 aspect-square flex flex-col items-center justify-center`}
                                  whileHover={{ scale: 1.05 }}
                                >
                                  <div className="text-2xl mb-1">{getWeaponIcon(weapon.type)}</div>
                                  <div className="text-xs font-medium text-white mb-1 truncate w-full">
                                    {weapon.name}
                                  </div>
                                  <div className="text-xs text-gray-300">
                                    +{weapon.attack}
                                  </div>
                                </motion.div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-4 text-gray-400">
                              <div className="text-2xl mb-2">⚔️</div>
                              <div className="text-sm">Keine verfügbaren Waffen</div>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Ausgerüstete Gegenstände aller Begleiter */}
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-3 text-center">
                      👥 Ausgerüstete Gegenstände
                    </h4>
                    <div className="space-y-4">
                      {companions.discovered.map((companion) => {
                        const equippedArmor = getCompanionArmor(companion.id);
                        const equippedWeapons = getCompanionWeapons(companion.id);
                        const totalEquipped = equippedArmor.length + equippedWeapons.length;
                        
                        if (totalEquipped === 0) return null;
                        
                        return (
                          <div key={companion.id} className="bg-gray-700 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <div className="text-lg">
                                {companion.icon.startsWith('data:') ? (
                                  <img 
                                    src={companion.icon} 
                                    alt={companion.name}
                                    className="w-6 h-6 object-contain"
                                    style={{ display: 'inline-block' }}
                                  />
                                ) : (
                                  companion.icon
                                )}
                              </div>
                              <div className="text-white font-medium">{companion.name}</div>
                              <div className="text-gray-400 text-sm">({totalEquipped} Gegenstände)</div>
                            </div>
                            
                            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
                              {equippedArmor.map((armor) => (
                                <motion.div
                                  key={armor.id}
                                  className={`bg-gradient-to-br ${getMaterialColor(armor.material)} rounded-lg p-3 text-center border border-gray-500 aspect-square flex flex-col items-center justify-center`}
                                  whileHover={{ scale: 1.05 }}
                                >
                                  <div className="text-2xl mb-1">{getArmorIcon(armor.type)}</div>
                                  <div className="text-xs font-medium text-white mb-1 truncate w-full">
                                    {armor.name}
                                  </div>
                                  <div className="text-xs text-gray-300">
                                    +{armor.defense}
                                  </div>
                                </motion.div>
                              ))}
                              
                              {equippedWeapons.map((weapon) => (
                                <motion.div
                                  key={weapon.id}
                                  className={`bg-gradient-to-br ${getMaterialColor(weapon.material)} rounded-lg p-3 text-center border border-gray-500 aspect-square flex flex-col items-center justify-center`}
                                  whileHover={{ scale: 1.05 }}
                                >
                                  <div className="text-2xl mb-1">{getWeaponIcon(weapon.type)}</div>
                                  <div className="text-xs font-medium text-white mb-1 truncate w-full">
                                    {weapon.name}
                                  </div>
                                  <div className="text-xs text-gray-300">
                                    +{weapon.attack}
                                  </div>
                                </motion.div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {selectedTab === 'experience' && (
              <motion.div
                key="experience"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-4"
              >
                <h3 className="text-xl font-bold text-white mb-4 text-center">⭐ XP-Börse</h3>
                
                {/* Begleiter-Auswahl für Erfahrung - Zusammenklappbar */}
                <div className="mb-4">
                  <motion.button
                    onClick={() => setShowExperienceCompanionSelection(!showExperienceCompanionSelection)}
                    className="w-full bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white rounded-lg p-3 font-medium transition-all duration-200 flex items-center justify-between"
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <div className="flex items-center gap-2">
                      <span>👥</span>
                      <span>
                        {selectedCompanion 
                          ? `Ausgewählt: ${companions.discovered.find(c => c.id === selectedCompanion)?.name}`
                          : 'Begleiter für Erfahrung auswählen'
                        }
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      {selectedCompanion && (
                        <div className="text-right text-sm">
                          {selectedXpType === 'normal' ? (
                            <>
                              <div className="text-yellow-300 font-bold">
                                Level {companions.discovered.find(c => c.id === selectedCompanion)?.level || 1}
                              </div>
                              <div className="text-yellow-200 text-xs mb-1">
                                {Math.floor(companions.discovered.find(c => c.id === selectedCompanion)?.experience || 0)} / {Math.floor((companions.discovered.find(c => c.id === selectedCompanion)?.level || 1) * 100)} XP
                              </div>
                              <div className="w-16 h-1.5 bg-gray-600 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full transition-all duration-300"
                                  style={{ 
                                    width: `${Math.min(100, ((companions.discovered.find(c => c.id === selectedCompanion)?.experience || 0) / ((companions.discovered.find(c => c.id === selectedCompanion)?.level || 1) * 100)) * 100)}%` 
                                  }}
                                />
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="text-red-300 font-bold">
                                Kampf Level {companions.discovered.find(c => c.id === selectedCompanion)?.battleLevel || 1}
                              </div>
                              <div className="text-red-200 text-xs mb-1">
                                {Math.floor(companions.discovered.find(c => c.id === selectedCompanion)?.battleExperience || 0)} / {Math.floor((companions.discovered.find(c => c.id === selectedCompanion)?.battleLevel || 1) * 100)} XP
                              </div>
                              <div className="w-16 h-1.5 bg-gray-600 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-gradient-to-r from-red-400 to-red-500 rounded-full transition-all duration-300"
                                  style={{ 
                                    width: `${Math.min(100, ((companions.discovered.find(c => c.id === selectedCompanion)?.battleExperience || 0) / ((companions.discovered.find(c => c.id === selectedCompanion)?.battleLevel || 1) * 100)) * 100)}%` 
                                  }}
                                />
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      <motion.div
                        animate={{ rotate: showExperienceCompanionSelection ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        ▼
                      </motion.div>
                    </div>
                  </motion.button>

                  <AnimatePresence key="experience-companion-selection">
                    {showExperienceCompanionSelection && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mt-3 overflow-hidden"
                      >
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                          {companions.discovered.map((companion) => (
                            <motion.button
                              key={companion.id}
                              onClick={() => {
                                setSelectedCompanion(companion.id);
                                setShowExperienceCompanionSelection(false);
                              }}
                              className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                                selectedCompanion === companion.id
                                  ? 'border-yellow-500 bg-yellow-600 bg-opacity-20'
                                  : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                              }`}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              <div className="flex flex-col items-center gap-2">
                                <div className="text-2xl">
                                  {companion.icon.startsWith('data:') ? (
                                    <img 
                                      src={companion.icon} 
                                      alt={companion.name}
                                      className="w-8 h-8 object-contain"
                                      style={{ display: 'inline-block' }}
                                    />
                                  ) : (
                                    companion.icon
                                  )}
                                </div>
                                <div className="text-center">
                                  <div className="text-sm font-medium text-white">{companion.name}</div>
                                  {selectedXpType === 'normal' ? (
                                    <>
                                      <div className="text-xs text-gray-400">Level {companion.level || 1}</div>
                                      <div className="text-xs text-yellow-400">
                                        {Math.floor(companion.experience || 0)} / {Math.floor((companion.level || 1) * 100)} XP
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <div className="text-xs text-gray-400">Kampf Level {companion.battleLevel || 1}</div>
                                      <div className="text-xs text-red-400">
                                        {Math.floor(companion.battleExperience || 0)} / {Math.floor((companion.battleLevel || 1) * 100)} XP
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            </motion.button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* XP-Typ-Auswahl */}
                {selectedCompanion && (
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-white mb-3 text-center">🎯 XP-Typ auswählen</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <motion.button
                        onClick={() => setSelectedXpType('normal')}
                        className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                          selectedXpType === 'normal'
                            ? 'border-blue-500 bg-blue-600 bg-opacity-20'
                            : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                        }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="flex flex-col items-center gap-2">
                          <div className="text-2xl">⭐</div>
                          <div className="text-center">
                            <div className="text-sm font-medium text-white">Normales Level</div>
                            <div className="text-xs text-gray-400">Standard-Fähigkeiten</div>
                          </div>
                        </div>
                      </motion.button>
                      
                      <motion.button
                        onClick={() => setSelectedXpType('arena')}
                        className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                          selectedXpType === 'arena'
                            ? 'border-red-500 bg-red-600 bg-opacity-20'
                            : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                        }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="flex flex-col items-center gap-2">
                          <div className="text-2xl">⚔️</div>
                          <div className="text-center">
                            <div className="text-sm font-medium text-white">Arena Level</div>
                            <div className="text-xs text-gray-400">Kampf-Fähigkeiten</div>
                          </div>
                        </div>
                      </motion.button>
                    </div>
                  </div>
                )}

                {/* Verfügbare Gegenstände zum Eintauschen */}
                {selectedCompanion && (
                  <div className="space-y-6">
                    {/* Verfügbare Rüstungen */}
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                        🛡️ Verfügbare Rüstungen zum Eintauschen ({getAvailableArmor().length})
                      </h4>
                      {getAvailableArmor().length > 0 ? (
                        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
                          {getAvailableArmor().map((armor) => {
                            const experienceValue = getItemExperienceValue(armor, 'armor');
                            return (
                              <motion.div
                                key={armor.id}
                                className={`bg-gradient-to-br ${getMaterialColor(armor.material)} rounded-lg p-3 text-center border border-gray-500 aspect-square flex flex-col items-center justify-center cursor-pointer`}
                                whileHover={{ scale: 1.05 }}
                                onClick={() => {
                                  if (tradeItemForExperience(armor.id, 'armor', selectedCompanion, selectedXpType)) {
                                    // Erfolgreich eingetauscht
                                  }
                                }}
                              >
                                <div className="text-2xl mb-1">{getArmorIcon(armor.type)}</div>
                                <div className="text-xs font-medium text-white mb-1 truncate w-full">
                                  {armor.name}
                                </div>
                                <div className="text-xs text-yellow-400 font-bold">
                                  +{experienceValue} XP
                                </div>
                                <div className="text-xs text-gray-300">
                                  +{armor.defense}
                                </div>
                              </motion.div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-gray-400">
                          <div className="text-2xl mb-2">🛡️</div>
                          <div className="text-sm">Keine verfügbaren Rüstungen</div>
                        </div>
                      )}
                    </div>

                    {/* Verfügbare Waffen */}
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                        ⚔️ Verfügbare Waffen zum Eintauschen ({getAvailableWeapons().length})
                      </h4>
                      {getAvailableWeapons().length > 0 ? (
                        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
                          {getAvailableWeapons().map((weapon) => {
                            const experienceValue = getItemExperienceValue(weapon, 'weapon');
                            return (
                              <motion.div
                                key={weapon.id}
                                className={`bg-gradient-to-br ${getMaterialColor(weapon.material)} rounded-lg p-3 text-center border border-gray-500 aspect-square flex flex-col items-center justify-center cursor-pointer`}
                                whileHover={{ scale: 1.05 }}
                                onClick={() => {
                                  if (tradeItemForExperience(weapon.id, 'weapon', selectedCompanion, selectedXpType)) {
                                    // Erfolgreich eingetauscht
                                  }
                                }}
                              >
                                <div className="text-2xl mb-1">{getWeaponIcon(weapon.type)}</div>
                                <div className="text-xs font-medium text-white mb-1 truncate w-full">
                                  {weapon.name}
                                </div>
                                <div className="text-xs text-yellow-400 font-bold">
                                  +{experienceValue} XP
                                </div>
                                <div className="text-xs text-gray-300">
                                  +{weapon.attack}
                                </div>
                              </motion.div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-gray-400">
                          <div className="text-2xl mb-2">⚔️</div>
                          <div className="text-sm">Keine verfügbaren Waffen</div>
                        </div>
                      )}
                    </div>


                  </div>
                )}

                {/* Kein Begleiter ausgewählt */}
                {!selectedCompanion && (
                  <div className="text-center py-8 text-gray-400">
                    <div className="text-3xl mb-3">⭐</div>
                    <div className="text-lg font-medium mb-2">Wähle einen Begleiter aus</div>
                    <div className="text-sm">Wähle einen Begleiter aus, um Gegenstände gegen Erfahrung einzutauschen</div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CompanionInventoryModal; 